import { fetchNinjaCsrf, getCachedNinjaCsrf } from '@/libs/ninja/csrf';
import { BACKEND_SERVER, BACKEND_SERVER_NINJA } from '@/settings';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { StatusEnum } from '../common-types';
import type { INewBrandGroup } from './brandGroupSlice';
import type { INewCategoryGroup } from './categoryGroupSlice';
import type { INewKeywordGroup } from './keywordGroupSlice';

export enum Screen {
  D = 'D',
  M = 'M',
  A = 'A',
}

export const screenTranslate: { [key in Screen]: string } = {
  [Screen.D]: 'Desktop',
  [Screen.M]: 'Mobile',
  [Screen.A]: 'App',
};

export enum Marketplace {
  Ozon = 'Ozon',
  Wildberries = 'Wildberries',
  YandexMarket = 'YandexMarket',
  Perekrestok = 'Perekrestok',
  Pyaterochka = 'Pyaterochka',
  Magnit = 'Magnit',
  MagnitCosmetic = 'MagnitCosmetic',
  Vprok = 'Vprok',
  Metro = 'Metro',
  DetMir = 'DetMir',
  MVideo = 'MVideo',
  Lenta = 'Lenta',
  GoldApple = 'GoldApple',
  VkusVill = 'VkusVill',
  KuperMagnit = 'KuperMagnit',
  KuperPyaterochka = 'KuperPyaterochka',
  KuperPerekrestok = 'KuperPerekrestok',
  KuperVprok = 'KuperVprok',
  KuperLenta = 'KuperLenta',
  KuperSuperLenta = 'KuperSuperLenta',
  KuperAuchan = 'KuperAuchan',
  Aptekaru = 'Aptekaru',
  EApteka = 'EApteka',
  Zdravcity = 'Zdravcity',
  Asna = 'Asna',
  Uteka = 'Uteka',
}

export const marketplaceTranslate: { [key in Marketplace]: string } = {
  [Marketplace.Ozon]: 'Ozon',
  [Marketplace.Wildberries]: 'Wildberries',
  [Marketplace.YandexMarket]: 'Яндекс Маркет',
  [Marketplace.Perekrestok]: 'Перекресток',
  [Marketplace.Pyaterochka]: 'Пятерочка',
  [Marketplace.Magnit]: 'Магнит',
  [Marketplace.MagnitCosmetic]: 'Магнит Косметик',
  [Marketplace.Vprok]: 'Впрок',
  [Marketplace.Metro]: 'Метро',
  [Marketplace.DetMir]: 'Детский Мир',
  [Marketplace.MVideo]: 'МВидео',
  [Marketplace.Lenta]: 'Лента',
  [Marketplace.GoldApple]: 'Золотое Яблоко',
  [Marketplace.VkusVill]: 'ВкусВилл',
  [Marketplace.KuperMagnit]: 'Купер Магнит',
  [Marketplace.KuperPyaterochka]: 'Купер Пятерочка',
  [Marketplace.KuperPerekrestok]: 'Купер Перекресток',
  [Marketplace.KuperVprok]: 'Купер Впрок',
  [Marketplace.KuperLenta]: 'Купер Лента',
  [Marketplace.KuperSuperLenta]: 'Купер Супер Лента',
  [Marketplace.KuperAuchan]: 'Купер Ашан',
  [Marketplace.Aptekaru]: 'АптекаRu',
  [Marketplace.EApteka]: 'E-Аптека',
  [Marketplace.Zdravcity]: 'Здравcити',
  [Marketplace.Asna]: 'Asna',
  [Marketplace.Uteka]: 'Uteka',
};

export interface IMarketplace {
  id?: number;
  marketplace_type: Marketplace;
  screen: Array<Screen>;
  region?: string[];
}

export interface IMarketplaceSettings {
  id?: number;
  marketplace_type: Marketplace;
  screens: Array<Screen>;
  regions?: string[];
}

export enum GeoMode {
  Region = 'region',
  Address = 'address',
}

export const geoModeTranslate: { [key in GeoMode]: string } = {
  [GeoMode.Region]: 'Регион',
  [GeoMode.Address]: 'Адресный сегмент',
};

/**
 * A device an address is served on. A device absent from `IAddressSegment.devices`
 * is not served at all, which is a different problem from one that exists but is
 * inactive — the two get different warnings in the UI.
 */
export interface IAddressSegmentDevice {
  screen: Screen;
  device_type: string;
  is_active: boolean;
}

export interface IAddressSegment {
  address_slug: string;
  address_name: string;
  platform: string;
  segment_slug: string;
  devices: IAddressSegmentDevice[];
}

export interface IProject {
  id: number;
  client: number;
  name: string;
  description: string;
  is_active: boolean;
  run_hours: Array<number>;
  start_date: string;
  end_date: string | null;
  marketplace: IMarketplace;
  brand_groups: Array<number>;
  keyword_groups: Array<number>;
  keywords_cnt: number;
  keywords_active_cnt: number;
  custom_proxies?: Array<number | { id: number; name: string }>;
  geo_mode?: GeoMode;
  address_slugs?: string[];
}

export interface INewProject {
  client: number;
  name: string;
  description: string;
  is_active: boolean;
  run_hours: Array<number>;
  start_date: string;
  end_date: string | null;
  marketplace_type_id: IMarketplace['id'];
  screen: Array<Screen>;
  region: string[];
  custom_proxy_ids?: number[];
  geo_mode?: GeoMode;
  address_slugs?: string[];
}

export interface IUpdateProject {
  id: number;
  client?: number;
  name?: string;
  description?: string;
  is_active?: boolean;
  run_hours?: Array<number>;
  start_date?: string;
  end_date?: string | null;
  marketplace_type_id?: IMarketplace['id'];
  screen?: Array<Screen>;
  region?: string[];
  marketplaceData?: IMarketplace;
  custom_proxy_ids?: number[];
  geo_mode?: GeoMode;
  address_slugs?: string[];
}

export interface IProjectsState {
  projects: IProject[];
  currentProject: IProject | null;
  status: StatusEnum;
  statusText: string | null;
  addressSegments: IAddressSegment[];
  addressSegmentsStatus: StatusEnum;
}

const initialState: IProjectsState = {
  projects: [],
  currentProject: null,
  status: StatusEnum.idle, // add status to handle loading state
  statusText: null, // add error to handle any error that may occur during the request
  addressSegments: [],
  addressSegmentsStatus: StatusEnum.idle,
};

export const getProjects = createAsyncThunk(
  'organization/getProjects',
  async (args: { slug: string; clientId?: number }) => {
    const params: { client?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/projects`,
      { params }
    );
    return response.data as IProject[];
  }
);

export const getProject = createAsyncThunk(
  'organization/getProject',
  async (args: { slug: string; projectId: number; clientId?: number }) => {
    const params: { client?: number } = {};
    if (args.clientId) {
      params.client = args.clientId;
    }
    const response = await axios.get(
      `${BACKEND_SERVER}/organizations/${args.slug}/projects/${args.projectId}`,
      { params }
    );
    return response.data as IProject;
  }
);

export const getAddressSegments = createAsyncThunk(
  'organization/getAddressSegments',
  async (args: { slug: string; platform?: string }) => {
    const params: { platform?: string } = {};
    if (args.platform) {
      params.platform = args.platform;
    }
    const response = await axios.get(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/address_segments`,
      { params }
    );
    return response.data as IAddressSegment[];
  }
);

export const createProject = createAsyncThunk(
  'organization/createProject',
  async (args: { slug: string; project: INewProject }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const response = await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/projects`,
      args.project,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return response.data as IProject;
  }
);

export const copyProject = createAsyncThunk(
  'organization/copyProject',
  async (args: {
    slug: string;
    project: INewProject;
    keywordGroups: INewKeywordGroup[];
    categoryGroups: INewCategoryGroup[];
    brandGroups: INewBrandGroup[];
  }) => {
    const responseProject = await axios.post(
      `${BACKEND_SERVER}/organizations/${args.slug}/projects/`,
      args.project
    );
    const newProject = responseProject.data as IProject;

    await Promise.all([
      ...args.keywordGroups.map((kwg) => {
        return axios.post(
          `${BACKEND_SERVER}/organizations/${args.slug}/keyword_groups/`,
          { ...kwg, project: newProject.id }
        );
      }),
      ...args.categoryGroups.map((cwg) => {
        return axios.post(
          `${BACKEND_SERVER}/organizations/${args.slug}/category_groups/`,
          { ...cwg, project: newProject.id }
        );
      }),
      ...args.brandGroups.map((bg) => {
        return axios.post(
          `${BACKEND_SERVER}/organizations/${args.slug}/brand_groups/`,
          { ...bg, project: newProject.id }
        );
      }),
    ]);

    return newProject;
  }
);

export const updateProject = createAsyncThunk(
  'organization/updateProject',
  async (args: {
    slug: string;
    project: IUpdateProject;
  }): Promise<IProject> => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    const { marketplaceData, ...payload } = args.project;
    const response = await axios.patch<IProject>(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/projects/${args.project.id}`,
      payload,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );

    const responseProject: IProject = response.data;

    return {
      ...responseProject,
      marketplace: marketplaceData ?? responseProject.marketplace,
    };
  }
);

export const deleteProject = createAsyncThunk(
  'organization/deleteProject',
  async (args: { slug: string; projectId: number }) => {
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    await axios.delete(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/projects/${args.projectId}`,
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return args.projectId;
  }
);

export const deleteProjects = createAsyncThunk(
  'organization/deleteProjects',
  async (args: { slug: string; projectIds: number[] }) => {
    // Backend will be adapted to accept either a single id or an array of ids
    const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
    await axios.post(
      `${BACKEND_SERVER_NINJA}/organizations/${args.slug}/projects/delete`,
      { project_ids: args.projectIds },
      { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
    );
    return args.projectIds as number[];
  }
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProject: (state, action: PayloadAction<IProject | null>) => {
      state.currentProject = action.payload;
    },
    setProjectIdle: (state) => {
      state.status = StatusEnum.idle;
      state.statusText = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getProjects
      .addCase(getProjects.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(getProjects.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        state.projects = [...action.payload].reverse();
        if (!state.currentProject) {
          state.currentProject = state.projects[0] ?? null;
        }
      })
      .addCase(getProjects.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          // TODO: hmm, navigate?
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      // getProject
      .addCase(getProject.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        getProject.fulfilled,
        (state, action: PayloadAction<IProject>) => {
          state.status = StatusEnum.succeeded;
          toast.success(state.statusText);
          state.currentProject = action.payload;
        }
      )
      .addCase(getProject.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          // TODO: hmm, navigate?
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      // getAddressSegments
      .addCase(getAddressSegments.pending, (state) => {
        state.addressSegmentsStatus = StatusEnum.loading;
      })
      .addCase(
        getAddressSegments.fulfilled,
        (state, action: PayloadAction<IAddressSegment[]>) => {
          state.addressSegmentsStatus = StatusEnum.succeeded;
          state.addressSegments = action.payload;
        }
      )
      .addCase(getAddressSegments.rejected, (state) => {
        // Non-fatal: the project form falls back to region mode only.
        state.addressSegmentsStatus = StatusEnum.failed;
        state.addressSegments = [];
      })
      // createProject
      .addCase(createProject.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        createProject.fulfilled,
        (state, action: PayloadAction<IProject>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Проект создан';
          toast.success(state.statusText);
          state.currentProject = action.payload;
          state.projects.unshift(action.payload);
        }
      )
      .addCase(createProject.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(copyProject.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        copyProject.fulfilled,
        (state, action: PayloadAction<IProject>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Проект скопирован';
          toast.success(state.statusText);
          state.currentProject = action.payload;
          state.projects.unshift(action.payload);
        }
      )
      .addCase(copyProject.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      // updateProject
      .addCase(updateProject.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        updateProject.fulfilled,
        (state, action: PayloadAction<IProject>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Проект обновлен';
          toast.success(state.statusText);
          const projectId = state.projects.findIndex(
            (c) => c.id === action.payload.id
          );
          if (projectId !== -1) {
            state.projects[projectId] = action.payload;
          }
        }
      )
      .addCase(updateProject.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;

        toast.error(state.statusText);
      })
      // deleteProject
      .addCase(deleteProject.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        deleteProject.fulfilled,
        (state, action: PayloadAction<number | undefined>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Проект удален';
          toast.success(state.statusText);
          if (action.payload) {
            const deletedProjectIndex = state.projects.findIndex(
              (c) => c.id === action.payload
            );
            if (deletedProjectIndex >= 0) {
              state.projects.splice(deletedProjectIndex, 1);
            } // TODO: else?
            if (action.payload === state.currentProject?.id) {
              state.currentProject = null;
            }
          } // TODO: else?
        }
      )
      .addCase(deleteProject.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        if (action.error.code === 'ERR_BAD_REQUEST') {
          // TODO: hmm, navigate?
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText);
      })
      // deleteProjects (bulk)
      .addCase(deleteProjects.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(
        deleteProjects.fulfilled,
        (state, action: PayloadAction<number[]>) => {
          state.status = StatusEnum.succeeded;
          state.statusText = 'Выбранные проекты удалены';
          toast.success(state.statusText);
          const idsToDelete = new Set(action.payload);
          state.projects = state.projects.filter((p) => !idsToDelete.has(p.id));
          if (
            state.currentProject &&
            idsToDelete.has(state.currentProject.id)
          ) {
            state.currentProject = null;
          }
        }
      )
      .addCase(deleteProjects.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      });
  },
});

export const { setProject, setProjectIdle } = projectsSlice.actions;

export default projectsSlice.reducer;
